import { useRef, useState } from "react";

/** Dos rangos son el mismo si dicen los mismos números: el padre arma el arreglo
 *  en cada render, así que comparar por identidad daría siempre "cambió". */
function mismoRango(a: number[], b: number[]) {
  return a.length === b.length && a.every((numero, i) => numero === b[i]);
}

/**
 * LO QUE LA PERSONA ELIGIÓ Y LA PROP TODAVÍA NO REFLEJA.
 *
 * Un control continuo cuyo cambio viaja por la url no puede mostrar la prop a
 * secas. La url no vuelve en el mismo pase: react-router v8 entrega su estado
 * DENTRO de `React.startTransition` (`RouterProvider`, en `lib/components.js`),
 * y una transición no es urgente, así que React pinta primero cualquier estado
 * local que se haya soltado y recién después el valor nuevo. En pantalla eso es
 * la manija saltando al valor anterior y volviendo medio segundo más tarde; con
 * la red lenta el salto dura más.
 *
 * LA PROP MANDA APENAS CAMBIA, y por eso hace falta recordar la anterior: sin
 * eso, sostener lo elegido se volvería ignorar al padre. "Limpiar" reescribe
 * todos los filtros de un saque, y un control pegado a la última elección diría
 * un rango mientras el servidor filtra por otro.
 *
 * SE ACEPTA una carrera: si la prop cambia sola en medio de un arrastre —una
 * escala que el servidor recalcula, la siembra escribiendo la url— la manija
 * salta bajo el dedo. Se probó a blindarla con una bandera de "gesto en curso" y
 * no se sostiene: Radix avisa el commit ANTES del change con teclado (medido:
 * `["commit:11,90","change:11,90"]`), así que toda máquina de estados armada con
 * esos dos avisos queda con un caso donde la bandera no se apaga nunca — y ahí
 * el control deja de seguir al padre para siempre, que es peor que un salto.
 */
export function useValorSostenido(valor: number[]) {
  const [elegido, setElegido] = useState<number[] | null>(null);
  const valorVisto = useRef(valor);

  /* El ajuste va en el RENDER y no en un `useEffect` —es el patrón que React
     documenta para "corregir estado cuando una prop cambia"—, porque un efecto
     pinta una vez con el valor viejo antes de corregirlo: el mismo parpadeo que
     este hook existe para no tener. */
  if (!mismoRango(valorVisto.current, valor)) {
    valorVisto.current = valor;
    if (elegido !== null) setElegido(null);
  }

  return { mostrado: elegido ?? valor, elegir: setElegido };
}
